import { ListOverlay, Row } from './ListOverlay.js';
import { diaHora } from '../lib/fechas.js';
import type { SessionState } from '../lib/session.js';
import { navigate } from '../lib/hash-route.js';
import { rotuloPeriodo } from '../lib/snapshot-types.js';

export interface EmailRow {
  id: string; ghl_email_id: string | null; user_email: string | null;
  /** Nombre de quien lo envio (o "Sin asignar"). */
  persona?: string;
  from_addr: string | null; to_addr: string | null;
  /** El primer destinatario que no es de casa. */
  para?: string | null;
  subject: string | null;
  direction: string | null;
  /** El contacto contesto (en los 14 dias siguientes al primer correo del periodo). */
  replied: boolean | null;
  opened: boolean | null;
  /** 'sin_verificar': aun no se ha podido comprobar contra GHL. */
  clase?: string;
  contact_id: string | null;
  created_at: string;
}

interface Props {
  session: SessionState;
  scope: 'me' | 'team';
  /** El filtro que aplico el snapshot: la lista son los mismos correos que suma la tarjeta. */
  filtro: URLSearchParams;
  locationId: string | null;
  filter: 'all' | 'replied';
}

/** La fecha de una fila: «mié 7 oct, 19:34» (el formato de toda la app, lib/fechas.ts). */
const cuando = (iso: string): string => diaHora(iso);

/**
 * Los correos ENVIADOS que cuentan (sin invitaciones de calendario, pruebas a
 * casa, avisos del sistema ni copias de borradores de Gmail), con su asunto real y
 * a quien fueron. "Respondieron" deja los de contactos que contestaron.
 */
export function EmailsList({ session, scope, filtro, locationId, filter }: Props) {
  const q = new URLSearchParams(filtro);
  q.set('max', '500');
  if (filter === 'replied') q.set('replied', '1');
  const path = `/api/dashboard/emails?${q.toString()}`;
  const desde = filtro.get('desde') ?? filtro.get('from');
  const hasta = filtro.get('hasta') ?? filtro.get('to');
  return (
    <ListOverlay<EmailRow>
      session={session}
      title={filter === 'replied' ? 'Correos · respondieron' : 'Correos enviados'}
      subtitle={
        <FilterTabs
          current={filter}
          onChange={(f) => navigate({ kind: 'emails-list', scope, filter: f })}
          periodo={desde && hasta ? rotuloPeriodo(desde, hasta) : ''}
        />
      }
      fetchPath={path}
      itemsKey="emails"
      emptyMessage={filter === 'replied' ? 'Nadie ha contestado a los correos de este periodo.' : 'Sin correos enviados en este periodo.'}
      onItemClick={(e) => {
        if (locationId && e.contact_id) {
          window.open(`https://app.gohighlevel.com/v2/location/${locationId}/contacts/detail/${e.contact_id}`, '_blank', 'noopener');
        }
      }}
      renderRow={(e) => <FilaCorreo correo={e} />}
    />
  );
}

/**
 * Una fila de la lista de correos: el asunto real, a quien fue ("Para:") y quien lo
 * envio. Un destinatario desconocido ("?" o vacio) no se pinta: no hay "Para: ?".
 */
export function FilaCorreo({ correo: e }: { correo: EmailRow }) {
  // "?" era lo que quedaba cuando no se sabia el destinatario: no se pinta.
  const para = [e.para, e.to_addr].find((x) => !!x && x.trim() !== '' && x.trim() !== '?') ?? null;
  return (
    <Row
      left={<>
        <div className="row-title">
          {e.subject?.trim() || (e.clase === 'sin_verificar' ? 'Sin comprobar todavía' : 'Sin asunto')}
          {e.replied && <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 5px', borderRadius: 3, background: 'var(--ok-soft)', color: 'var(--ok)' }}>respondió</span>}
          {e.clase === 'sin_verificar' && <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 5px', borderRadius: 3, background: 'var(--surface-hi)', color: 'var(--fg-muted)' }} title="Aún no se ha leído el detalle de GHL">sin verificar</span>}
        </div>
        {para ? <div className="row-sub">Para: {para}</div> : null}
      </>}
      aside={<>
        <div>{e.persona ?? e.user_email ?? 'Sin asignar'}</div>
        <div>{cuando(e.created_at)}</div>
      </>}
    />
  );
}

function FilterTabs({ current, onChange, periodo }: { current: 'all' | 'replied'; onChange: (f: 'all' | 'replied') => void; periodo: string }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
      <button className={`tab${current === 'all' ? ' active' : ''}`} onClick={() => onChange('all')}>Todos</button>
      <button className={`tab${current === 'replied' ? ' active' : ''}`} onClick={() => onChange('replied')}>Respondieron</button>
      {periodo && <span style={{ color: 'var(--fg-subtle)', fontSize: 11 }}>· {periodo}</span>}
    </div>
  );
}
